import { AloftArea, AloftPrice } from '@aloft/shared-objects';
import { isUndefined } from '@aloft/toolkit-shared';

import { createAppAsyncThunk } from '../../createAppAsyncThunk.ts';
import { SubjectPropertyDataState } from '../../types.ts';
import { selectCompSequence } from '../selectors/orderSelectors.ts';
import { selectImprovementsCost } from '../selectors/selectImprovementsCost.ts';
import { selectSiteSizes } from '../selectors/selectSiteSize.ts';
import { selectSiteValueRatios } from '../selectors/selectSiteValueRatio.ts';

export const setInitialOpinionOfSiteValue = createAppAsyncThunk(
  'opinionOfSiteValue/setInitialValue',
  (_, { getState, rejectWithValue }) => {
    const state = getState();
    const compSequence = selectCompSequence(state);
    const siteValueRatios = selectSiteValueRatios(state);
    const siteSizes = selectSiteSizes(state);
    const subjectImprovementsCost = selectImprovementsCost(state, 'subject');

    const { opinionOfSiteValue } = (state.propertyData.entities.subject as SubjectPropertyDataState)
      .subjectData.editableData;
    if (opinionOfSiteValue && opinionOfSiteValue !== AloftPrice.zero.toJSON()) {
      return rejectWithValue(opinionOfSiteValue);
    }

    const subjectSiteArea = siteSizes.subject;
    if (!subjectSiteArea || !subjectImprovementsCost) return AloftPrice.zero.toJSON();

    // Find the cost/sqft of the comp with the closest site area to the subject
    const closestComp = compSequence.reduce(
      (acc: { siteValueRatio: number; sizeDiff: number } | undefined, curr) => {
        const currArea = siteSizes[curr];
        const siteValueRatio = siteValueRatios[curr];
        if (!currArea || isUndefined(siteValueRatio) || currArea.equals(AloftArea.zero)) return acc;

        const sizeDiff = Math.abs(subjectSiteArea.sub(currArea).getUnitAmount('sqft'));

        if (!acc || sizeDiff < acc.sizeDiff) return { siteValueRatio, sizeDiff };
        return acc;
      },
      undefined,
    );

    if (!closestComp) return AloftPrice.zero.toJSON();

    const { siteValueRatio } = closestComp;
    if (siteValueRatio === 1) return AloftPrice.zero.toJSON();

    return subjectImprovementsCost
      .div(1 - siteValueRatio)
      .sub(subjectImprovementsCost)
      .toJSON();
  },
);
